import React, { useState, useEffect, useRef } from "react";

import { PersonOutline } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { uploadBase64ImageToS3 } from "../../../../../util/uploadBase64ImageToS3";
import { fileIsValid, DEFAULT_ALLOWED_IMAGE_EXTENSIONS } from "../../../../../util/fileIsValid";
import { File as IFile } from "../../../../../models/file";
import { Button } from "../../../../Form";
import { Color } from "../../../../Form/Button";
import { NotificationToast } from "../../../Toast/NotificationToast";
import {
  AvatarWrapper,
  AvatarImage,
  AvatarComponent,
  AvatarInput,
  TextWrapper,
  ButtonWrapper,
} from "./styles";

enum MaxFileNumber {
  MB = "2 MB",
}

enum FileType {
  JPG = "JPG",
  PNG = "PNG",
}

/* Maximum allowed size in bytes 2MB */
const MAX_ALLOWED_SIZE = 2 * 1024 * 1024;

export { DEFAULT_ALLOWED_IMAGE_EXTENSIONS as ALLOWED_IMAGE_EXTENSIONS } from "../../../../../util/fileIsValid";

type UploadMode = "base64" | "s3";

type Props = {
  allowedImageExtensions?: string[];
  onChangePicture?: (value?: string) => void;
  picture?: string;
  name?: string;
  isOnViewMode?: boolean;
  isOnProfilePage?: boolean;
  uploadMode?: UploadMode;
};

export const AvatarGeneric = (props: Props) => {
  const {
    allowedImageExtensions,
    picture,
    onChangePicture,
    name,
    isOnProfilePage = false,
    isOnViewMode = false,
    uploadMode = "base64",
  } = props;

  const [avatarImage, setAvatarImage] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { t: translate } = useTranslation();
  const imgRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (picture) setAvatarImage(picture && picture.includes("https://") ? picture : avatarImage);
    else setAvatarImage("");
  }, [picture]);

  const imageIsValid = (image?: IFile) => {
    return fileIsValid(translate, image, {
      allowedExtensions: allowedImageExtensions,
      maxSize: MAX_ALLOWED_SIZE,
    });
  };

  const handleChangeImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target && event?.target?.files && event?.target?.files?.length > 0) {
      const image = event?.target?.files[0];

      const type = image.type.split("/");
      const extension = type.length > 0 ? type[1] : image.type;
      const normalizedExtension = extension?.toLowerCase();

      const fileToSave = {
        extension: normalizedExtension,
        size: image.size,
      } as IFile;

      if (image && imageIsValid(fileToSave)) {
        const reader = new FileReader();

        reader.onloadend = async () => {
          const resultString = reader.result ? reader.result.toString() : "";
          const [prefix, base64Data] = resultString.split(",");

          if (uploadMode === "s3") {
            if (!base64Data) {
              NotificationToast.error(translate("anErrorOccurredTryAgain"));
              setAvatarImage("");
              onChangePicture?.(undefined);
              event.target.value = "";
              return;
            }

            setAvatarImage(resultString);
            setIsUploading(true);

            try {
              const link = await uploadBase64ImageToS3(base64Data, normalizedExtension || "png", {
                pathPrefix: "user-picture",
              });
              setAvatarImage(link);
              onChangePicture?.(link);
            } catch (error) {
              console.error("Error uploading avatar to S3", error);
              NotificationToast.error(translate("anErrorOccurredTryAgain"));
              setAvatarImage("");
              onChangePicture?.(undefined);
            } finally {
              setIsUploading(false);
            }
          } else {
            setAvatarImage(resultString);
            if (onChangePicture && base64Data) onChangePicture(base64Data);
          }

          event.target.value = "";
        };
        reader.onerror = () => {
          setAvatarImage("");
          NotificationToast.error(translate("anErrorOccurredTryAgain"));
          event.target.value = "";
        };
        reader.readAsDataURL(image);
      }
    }

    if (event?.target) {
      event.target.value = "";
    }
  };

  return (
    <div>
      {avatarImage ? (
        <div>
          <AvatarImage src={avatarImage} />
          {!isOnViewMode ? (
            <ButtonWrapper>
              <input
                ref={imgRef}
                type="file"
                accept={
                  allowedImageExtensions
                    ? allowedImageExtensions?.map((extension) => `.${extension}`).join(", ")
                    : "image/*"
                }
                onChange={handleChangeImage}
                disabled={isUploading}
              />
              <Button
                customColor={Color.TEXT_INFO}
                onClick={() => {
                  setAvatarImage("");
                  if (onChangePicture) onChangePicture(undefined);
                }}
                disabled={isUploading}
              >
                {translate("remove")}
              </Button>
              <Button
                customColor={Color.SECONDARY_DARK}
                onClick={() => imgRef.current?.click()}
                disabled={isUploading}
              >
                {translate("editPhoto")}
              </Button>
            </ButtonWrapper>
          ) : null}
        </div>
      ) : (
        <AvatarWrapper>
          <AvatarComponent
            isOnViewMode={isOnViewMode}
            onClick={() => {
              if (!isOnViewMode && !isUploading) imgRef.current?.click();
            }}
          >
            <PersonOutline />
            <AvatarInput
              ref={imgRef}
              id="inputPhoto"
              disabled={isOnViewMode || isUploading}
              type="file"
              name={name}
              accept={
                allowedImageExtensions
                  ? allowedImageExtensions?.map((extension) => `.${extension}`).join(", ")
                  : "image/*"
              }
              onChange={handleChangeImage}
            />
          </AvatarComponent>
          {!isOnProfilePage && (
            <TextWrapper>
              <p>{translate("choosePicture")}</p>
              <p className="subtext">
                {translate("maxFileSize")} <strong>{MaxFileNumber.MB}</strong>
              </p>
              <p className="subtext">
                {translate("fileTypes")} <strong>
                  {FileType.JPG}, {FileType.PNG}
                </strong>
              </p>
            </TextWrapper>
          )}
        </AvatarWrapper>
      )}
    </div>
  );
};
